import { useState, type ReactNode } from 'react';
import { Button, Input, Select, Textarea } from '@/components/ui';
import type { TemplateButton } from '@/api/templates';
import { MediaPicker } from '@/components/features/media/MediaPicker';
import { RecommendationsScenarioPreview } from '@/features/okdoc/admin/recommendations/RecommendationsScenarioPreview';
import {
  OKDOC_CTA_ACTION_OPTIONS,
  OKDOC_INTERACTION_INTENT_OPTIONS,
} from '@/features/okdoc/registry/ctaRegistry';
import type {
  OkdocScenarioCTA,
  OkdocScenarioProfile,
  OkdocScenarioSection,
  OkdocScenarioSectionEntry,
} from '@/features/okdoc/types';

interface OkdocScenarioProfileEditorProps {
  value?: OkdocScenarioProfile;
  buttons?: TemplateButton[];
  onChange: (value?: OkdocScenarioProfile) => void;
  testId?: string;
  allowManualCTAs?: boolean;
  alwaysEnabled?: boolean;
  previewContent?: ReactNode;
  tenantId?: string;
}

const COMPONENT_OPTIONS = [
  { value: 'checklist', label: 'Чек-лист' },
  { value: 'numbered_list', label: '1-2-3 список' },
  { value: 'timeline', label: 'Таймлайн' },
];

function createDefaultEntry(): OkdocScenarioSectionEntry {
  return {
    title: '',
    description: '',
    meta: '',
  };
}

function createDefaultSection(): OkdocScenarioSection {
  return {
    title: '',
    description: '',
    component: 'checklist',
    entries: [createDefaultEntry()],
  };
}

function createDefaultProfile(): OkdocScenarioProfile {
  return {
    kind: 'recommendations',
    shell_title: 'Рекомендации после приёма',
    shell_subtitle: 'Персональные материалы и следующие шаги после визита собраны на одной странице',
    title: 'Рекомендации после приёма',
    subtitle: '',
    description: '',
    badge: 'После приёма',
    cta_title: 'Полезные действия',
    cta_description: 'Откройте персональные материалы и нужные ссылки по Вашему сценарию.',
    variant: 'template_custom',
    sections: [createDefaultSection()],
    ctas: [],
  };
}

function createDefaultCTA(): OkdocScenarioCTA {
  return {
    id: '',
    target: '',
    label: '',
    action_kind: 'read_recommendations',
    interaction_intent: 'care_followup',
    url: '',
    description: '',
  };
}

function buildPreviewCTAs(buttons: TemplateButton[] = []): OkdocScenarioCTA[] {
  return buttons
    .filter((button) => !!button.text)
    .map((button, index) => ({
      id: `cta_${index + 1}`,
      target: `cta_${index + 1}`,
      label: button.text,
      kind: button.type,
      action: button.callback_data,
      action_kind: 'read_recommendations',
      interaction_intent: 'care_followup',
      url: button.url,
    }));
}

export function OkdocScenarioProfileEditor({
  value,
  buttons = [],
  onChange,
  testId,
  allowManualCTAs = false,
  alwaysEnabled = false,
  previewContent,
  tenantId,
}: OkdocScenarioProfileEditorProps) {
  // 06.04.26: gallery picker привязывается к конкретной секции, чтобы URL/alt
  // записывались сразу в scenario_profile и preview видел ровно те же данные,
  // которые уйдут в runtime definition.
  const [pickerSectionIndex, setPickerSectionIndex] = useState<number | null>(null);
  const isEnabled = alwaysEnabled || !!value;
  const profile = value || createDefaultProfile();
  const previewProfile: OkdocScenarioProfile | undefined = isEnabled
    ? {
        ...profile,
        ctas:
          profile.ctas && profile.ctas.length > 0
            ? profile.ctas
            : allowManualCTAs
              ? []
              : buildPreviewCTAs(buttons),
      }
    : undefined;

  const updateProfile = (patch: Partial<OkdocScenarioProfile>) => {
    onChange({ ...profile, ...patch });
  };

  const updateSection = (sectionIndex: number, patch: Partial<OkdocScenarioSection>) => {
    const nextSections = [...(profile.sections || [])];
    nextSections[sectionIndex] = { ...nextSections[sectionIndex], ...patch };
    updateProfile({ sections: nextSections });
  };

  const updateSectionEntry = (sectionIndex: number, entryIndex: number, patch: Partial<OkdocScenarioSectionEntry>) => {
    const nextSections = [...(profile.sections || [])];
    const section = nextSections[sectionIndex] || createDefaultSection();
    const nextEntries = [...(section.entries || [])];
    nextEntries[entryIndex] = { ...nextEntries[entryIndex], ...patch };
    nextSections[sectionIndex] = { ...section, entries: nextEntries };
    updateProfile({ sections: nextSections });
  };

  const addSection = () => {
    updateProfile({ sections: [...(profile.sections || []), createDefaultSection()] });
  };

  const removeSection = (sectionIndex: number) => {
    updateProfile({ sections: (profile.sections || []).filter((_, index) => index !== sectionIndex) });
  };

  const addEntry = (sectionIndex: number) => {
    const nextSections = [...(profile.sections || [])];
    const section = nextSections[sectionIndex] || createDefaultSection();
    nextSections[sectionIndex] = {
      ...section,
      entries: [...(section.entries || []), createDefaultEntry()],
    };
    updateProfile({ sections: nextSections });
  };

  const removeEntry = (sectionIndex: number, entryIndex: number) => {
    const nextSections = [...(profile.sections || [])];
    const section = nextSections[sectionIndex] || createDefaultSection();
    nextSections[sectionIndex] = {
      ...section,
      entries: (section.entries || []).filter((_, index) => index !== entryIndex),
    };
    updateProfile({ sections: nextSections });
  };

  const updateCTA = (ctaIndex: number, patch: Partial<OkdocScenarioCTA>) => {
    const nextCTAs = [...(profile.ctas || [])];
    nextCTAs[ctaIndex] = { ...nextCTAs[ctaIndex], ...patch };
    updateProfile({ ctas: nextCTAs });
  };

  const addCTA = () => {
    updateProfile({ ctas: [...(profile.ctas || []), createDefaultCTA()] });
  };

  const removeCTA = (ctaIndex: number) => {
    updateProfile({ ctas: (profile.ctas || []).filter((_, index) => index !== ctaIndex) });
  };

  return (
    <div className="space-y-4" data-testid={testId || 'okdoc-scenario-profile-editor'}>
      {!alwaysEnabled && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => onChange(e.target.checked ? createDefaultProfile() : undefined)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <div className="text-sm font-medium text-neutral-900">OKDOC-профиль рекомендаций</div>
              <div className="mt-1 text-xs text-neutral-600">
                Включите, чтобы сохранить component-aware landing прямо в шаблоне. Если выключено, runtime использует tenant
                preset или generic fallback.
              </div>
            </div>
          </label>
        </div>
      )}

      {isEnabled && (
        <div className="space-y-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Заголовок shell"
              value={profile.shell_title || ''}
              onChange={(e) => updateProfile({ shell_title: e.target.value })}
              placeholder="Рекомендации после приёма"
            />
            <Input
              label="Подзаголовок shell"
              value={profile.shell_subtitle || ''}
              onChange={(e) => updateProfile({ shell_subtitle: e.target.value })}
              placeholder="Персональные материалы и следующие шаги после визита собраны на одной странице"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Badge"
              value={profile.badge || ''}
              onChange={(e) => updateProfile({ badge: e.target.value })}
              placeholder="После приёма"
            />
            <Input
              label="Variant"
              value={profile.variant || ''}
              onChange={(e) => updateProfile({ variant: e.target.value })}
              placeholder="template_custom"
            />
          </div>

          <Input
            label="Заголовок landing"
            value={profile.title || ''}
            onChange={(e) => updateProfile({ title: e.target.value })}
            placeholder="Рекомендации после приёма"
          />

          <Input
            label="Подзаголовок"
            value={profile.subtitle || ''}
            onChange={(e) => updateProfile({ subtitle: e.target.value })}
            placeholder="{{service_name}}"
          />

          <Textarea
            label="Описание"
            value={profile.description || ''}
            onChange={(e) => updateProfile({ description: e.target.value })}
            rows={3}
            placeholder="Мы собрали для Вас полезные шаги после визита."
          />

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Заголовок блока CTA"
              value={profile.cta_title || ''}
              onChange={(e) => updateProfile({ cta_title: e.target.value })}
              placeholder="Полезные действия"
            />
            <Input
              label="Описание блока CTA"
              value={profile.cta_description || ''}
              onChange={(e) => updateProfile({ cta_description: e.target.value })}
              placeholder="Откройте персональные материалы и нужные ссылки по Вашему сценарию."
            />
          </div>

          <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-3 text-xs text-neutral-600">
            Можно использовать переменные шаблона: `{'{{patient_name}}'}`, `{'{{service_name}}'}`, `{'{{doctor_name}}'}`, `{'{{cluster_name}}'}` и др.
            {allowManualCTAs
              ? ' CTA-кнопки сохраняются прямо в landing definition и используются в runtime/preview.'
              : ' CTA-кнопки берутся из `buttons` шаблона и автоматически подтягиваются в OKDOC-preview.'}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-neutral-900">Секции landing</div>
              <Button type="button" variant="secondary" size="sm" onClick={addSection}>
                Добавить секцию
              </Button>
            </div>

            {(profile.sections || []).map((section, sectionIndex) => (
              <div key={`${section.id || 'section'}-${sectionIndex}`} className="space-y-3 rounded-lg border border-neutral-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-neutral-900">Секция {sectionIndex + 1}</div>
                  <Button type="button" variant="secondary" size="sm" onClick={() => removeSection(sectionIndex)}>
                    Удалить секцию
                  </Button>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="Заголовок секции"
                    value={section.title || ''}
                    onChange={(e) => updateSection(sectionIndex, { title: e.target.value })}
                    placeholder="Что сделать сейчас"
                  />
                  <Select
                    label="Тип компонента"
                    value={section.component || 'checklist'}
                    onChange={(e) => updateSection(sectionIndex, { component: e.target.value })}
                    options={COMPONENT_OPTIONS}
                  />
                </div>

                <Textarea
                  label="Описание секции"
                  value={section.description || ''}
                  onChange={(e) => updateSection(sectionIndex, { description: e.target.value })}
                  rows={2}
                  placeholder="Короткое пояснение для пациента."
                />

                <div className="grid gap-4 md:grid-cols-3">
                  <Input
                    label="Image URL"
                    value={section.image?.url || ''}
                    onChange={(e) => updateSection(sectionIndex, { image: { ...section.image, url: e.target.value } })}
                    placeholder="https://..."
                  />
                  <Input
                    label="Image Alt"
                    value={section.image?.alt || ''}
                    onChange={(e) => updateSection(sectionIndex, { image: { ...section.image, alt: e.target.value } })}
                    placeholder="Описание изображения"
                  />
                  <Input
                    label="Image Caption"
                    value={section.image?.caption || ''}
                    onChange={(e) => updateSection(sectionIndex, { image: { ...section.image, caption: e.target.value } })}
                    placeholder="Подпись под изображением"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setPickerSectionIndex(sectionIndex)}
                  >
                    Выбрать из галереи
                  </Button>
                </div>

                <div className="space-y-3 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-neutral-800">Элементы секции</div>
                    <Button type="button" variant="secondary" size="sm" onClick={() => addEntry(sectionIndex)}>
                      Добавить элемент
                    </Button>
                  </div>

                  {(section.entries || []).map((entry, entryIndex) => (
                    <div key={`${entry.id || 'entry'}-${entryIndex}`} className="space-y-3 rounded-lg border border-neutral-200 bg-white p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">Элемент {entryIndex + 1}</div>
                        <Button type="button" variant="secondary" size="sm" onClick={() => removeEntry(sectionIndex, entryIndex)}>
                          Удалить
                        </Button>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <Input
                          label="Title"
                          value={entry.title || ''}
                          onChange={(e) => updateSectionEntry(sectionIndex, entryIndex, { title: e.target.value })}
                          placeholder="Короткий заголовок"
                        />
                        <Input
                          label="Meta"
                          value={entry.meta || ''}
                          onChange={(e) => updateSectionEntry(sectionIndex, entryIndex, { meta: e.target.value })}
                          placeholder="48 часов / День операции / и т.д."
                        />
                      </div>
                      <Textarea
                        label="Description"
                        value={entry.description || ''}
                        onChange={(e) => updateSectionEntry(sectionIndex, entryIndex, { description: e.target.value })}
                        rows={2}
                        placeholder="Основной текст элемента."
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {allowManualCTAs && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-neutral-900">CTA-кнопки</div>
                <Button type="button" variant="secondary" size="sm" onClick={addCTA}>
                  Добавить CTA
                </Button>
              </div>

              {(profile.ctas || []).length > 0 ? (
                (profile.ctas || []).map((cta, ctaIndex) => (
                  <div key={`${cta.id || 'cta'}-${ctaIndex}`} className="space-y-3 rounded-lg border border-neutral-200 bg-white p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-neutral-900">CTA {ctaIndex + 1}</div>
                      <Button type="button" variant="secondary" size="sm" onClick={() => removeCTA(ctaIndex)}>
                        Удалить CTA
                      </Button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <Input
                        label="Label"
                        value={cta.label || ''}
                        onChange={(e) => updateCTA(ctaIndex, { label: e.target.value })}
                        placeholder="Открыть материал"
                      />
                      <Input
                        label="Target"
                        value={cta.target || ''}
                        onChange={(e) => updateCTA(ctaIndex, { target: e.target.value })}
                        placeholder="default / cta_1 / review"
                      />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <Select
                        label="Action kind"
                        value={cta.action_kind || ''}
                        onChange={(e) => updateCTA(ctaIndex, { action_kind: e.target.value })}
                        options={OKDOC_CTA_ACTION_OPTIONS}
                      />
                      <Select
                        label="Interaction intent"
                        value={cta.interaction_intent || ''}
                        onChange={(e) => updateCTA(ctaIndex, { interaction_intent: e.target.value })}
                        options={OKDOC_INTERACTION_INTENT_OPTIONS}
                      />
                    </div>

                    <Input
                      label="URL"
                      value={cta.url || ''}
                      onChange={(e) => updateCTA(ctaIndex, { url: e.target.value })}
                      placeholder="https://..."
                    />

                    <Textarea
                      label="Description"
                      value={cta.description || ''}
                      onChange={(e) => updateCTA(ctaIndex, { description: e.target.value })}
                      rows={2}
                      placeholder="Необязательное описание CTA."
                    />
                  </div>
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-3 text-sm text-neutral-500">
                  CTA пока не добавлены.
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <div className="text-sm font-medium text-neutral-900">Live preview</div>
            {previewContent || <RecommendationsScenarioPreview profile={previewProfile} />}
          </div>
        </div>
      )}

      <MediaPicker
        isOpen={pickerSectionIndex !== null}
        tenantId={tenantId}
        onClose={() => setPickerSectionIndex(null)}
        onSelect={(asset) => {
          if (pickerSectionIndex === null) {
            return;
          }
          const currentSection = profile.sections?.[pickerSectionIndex];
          updateSection(pickerSectionIndex, {
            image: {
              ...currentSection?.image,
              url: asset.public_url || currentSection?.image?.url || '',
              alt: asset.alt_text || currentSection?.image?.alt || asset.title || asset.original_filename,
            },
          });
        }}
      />
    </div>
  );
}
